// The recording side of live listening, like the app's LiveShare (app/.../sync/LiveShare.kt).
// State frames tell the chat that someone records ("audio": it can be listened to, "text": the live
// transcript runs), every 4 s and on every change. Opus packets go out only while someone listens
// (a listener counts for 10 s after its last "listen"), bundled as [uint16 big-endian length][packet]
// … up to 10 packets (200 ms) or 4000 bytes, base64 in {"t":"live","packets","seq"}.

import { toBase64 } from "../data/bytes.ts";

const PACKETS_PER_BUNDLE = 10;
const MAX_BUNDLE_BYTES = 4_000;
const LISTENER_TTL_MS = 10_000;
const KEEPALIVE_MS = 4_000;

export class LiveShare {
  private readonly listeners = new Map<string, number>();
  private bundle: Uint8Array[] = [];
  private bundleBytes = 0;
  private seq = 0;
  private audio = false;
  private text = false;
  private state: "recording" | "paused" = "recording";
  private readonly timer: ReturnType<typeof setInterval>;
  private readonly send: (frame: Record<string, unknown>) => void;
  private readonly conversation: string;
  private readonly now: () => number;

  constructor(conversation: string, send: (frame: Record<string, unknown>) => void, now: () => number = Date.now) {
    this.conversation = conversation;
    this.send = send;
    this.now = now;
    this.sendState();
    this.timer = setInterval(() => {
      for (const [user, until] of this.listeners) if (until < this.now()) this.listeners.delete(user);
      this.sendState();
    }, KEEPALIVE_MS);
  }

  get listening(): number {
    return this.listeners.size;
  }

  /** {"t":"live","listening":true|false,"user":<listener>} addressed to us. */
  onListener(user: string, listening: boolean) {
    if (listening) this.listeners.set(user, this.now() + LISTENER_TTL_MS);
    else this.listeners.delete(user);
  }

  setText(text: boolean) {
    this.text = text;
    this.sendState();
  }

  setPaused(paused: boolean) {
    this.state = paused ? "paused" : "recording";
    this.sendState();
  }

  packet(packet: Uint8Array) {
    if (!this.audio) {
      this.audio = true;
      this.sendState();
    }
    if (this.listeners.size === 0) return;
    this.bundle.push(packet);
    this.bundleBytes += 2 + packet.length;
    if (this.bundle.length >= PACKETS_PER_BUNDLE || this.bundleBytes >= MAX_BUNDLE_BYTES) this.flush();
  }

  /** The recording ended ("ended") or was thrown away ("discarded"). */
  end(state: "ended" | "discarded") {
    clearInterval(this.timer);
    if (state === "ended") this.flush();
    this.send({ t: "live", conversation: this.conversation, state });
  }

  private flush() {
    if (this.bundle.length === 0) return;
    const out = new Uint8Array(this.bundleBytes);
    let at = 0;
    for (const p of this.bundle) {
      out[at] = p.length >> 8;
      out[at + 1] = p.length & 0xff;
      out.set(p, at + 2);
      at += 2 + p.length;
    }
    this.bundle = [];
    this.bundleBytes = 0;
    this.send({ t: "live", conversation: this.conversation, packets: toBase64(out), seq: ++this.seq });
  }

  private sendState() {
    this.send({ t: "live", conversation: this.conversation, state: this.state, audio: this.audio, text: this.text });
  }
}

/** The packets of a bundle; stops at the first one cut short (as the app's listener). */
export function splitBundle(bundle: Uint8Array): Uint8Array[] {
  const out: Uint8Array[] = [];
  let at = 0;
  while (at + 2 <= bundle.length) {
    const length = (bundle[at]! << 8) | bundle[at + 1]!;
    if (at + 2 + length > bundle.length) break;
    out.push(bundle.subarray(at + 2, at + 2 + length));
    at += 2 + length;
  }
  return out;
}
